import { gql, useLazyQuery } from '@apollo/client'
import { FilterList } from '@mui/icons-material'
import {
  Grid,
  Toolbar,
  Typography,
  Button,
  useMediaQuery,
  AccordionDetails,
  AccordionSummary,
  Accordion,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { Retailer } from '@promoboxx/graphql-gateway-types'
import { FilterInfo } from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import RetailerAccountBrandAutocomplete, {
  RetailerAccountBrandAutocompleteBrand,
} from '@src/components/shared/Filter/RetailerAccountBrandAutocomplete'
import SearchableFilter from '@src/components/shared/Filter/SearchableFilter'
import Search from '@src/components/shared/Search'
import useDebounceValue from '@src/hooks/useDebounceValue'
import booleanFilter from '@src/lib/booleanFilter'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'
import { currentRetailerIdSelector } from '@src/stores/retailers/selectors'

const filterByChannel = [
  { label: I18n.t('filters.popular'), value: 'popular' },
  { label: I18n.t('filters.facebook'), value: 'facebook' },
  { label: I18n.t('filters.twitter'), value: 'twitter' },
  { label: I18n.t('filters.instagram'), value: 'instagram' },
  { label: I18n.t('filters.email'), value: 'email' },
  { label: I18n.t('filters.downloadable'), value: 'asset' },
  { label: I18n.t('filters.webBanner'), value: 'embed' },
]

const GET_CAMPAIGNS_TAGS = gql`
  query GetCampaignsTagsContentPageFilters(
    $resourceType: CampaignResourceType!
    $resourceId: ID!
  ) {
    getCampaignTags(resourceType: $resourceType, resourceId: $resourceId) {
      tags
    }
  }
`

const ContentFilters: React.FC<
  React.PropsWithChildren<{
    onChange: (
      searchValue: string,
      debouncedSearchValue?:
        | string
        | string[]
        | RetailerAccountBrandAutocompleteBrand[],
    ) => void
    onClear?: () => void
    hideFilters?: string[]
    brandFilter?: string
    filterInfo: FilterInfo<{
      filterByChannel?: string | string[]
      brands: string[]
      showValue?: string[]
      tagsValues?: string[]
      searchValue?: string | null
    }>
  }>
> = ({
  onChange,
  onClear = () => {},
  hideFilters = [],
  filterInfo,
  brandFilter,
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const retailerId = useSelector(currentRetailerIdSelector)
  const [getCampaignTags, { data, refetch, called }] =
    useLazyQuery(GET_CAMPAIGNS_TAGS)
  const debouncedSearchValue = useDebounceValue(searchValue, 500) //It's really that simple? :P
  const showItems =  [{ label: I18n.t('filters.current'), value: 'active' }]


  useEffect(() => {
    const variables = {
      resourceId: retailerId ? retailerId : retailerAccount.id,
      resourceType: retailerId ? 'retailers' : 'retailer_accounts',
    }
    ;(called ? refetch(variables) : getCampaignTags({ variables })).catch(
      (error) => {
        console.error(error)
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerId, retailerAccount])

  useEffect(() => {
    if (debouncedSearchValue !== null) {
      onChange('searchValue', debouncedSearchValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue])

  const onChangeSearchValue = (newSearchValue: string | null) => {
    setSearchValue(newSearchValue)
  }

  const tags = (data && data.getCampaignTags && data.getCampaignTags.tags) || []

  const tagsItems =
    (tags &&
      tags.map((tag: string) => {
        return {
          label: tag,
          value: tag,
        }
      })) ||
    []

  const idMap = useMemo(() => {
    const brandToRetailer: Record<string, string> = {}
    const retailerToBrand: Record<string, string> = {}
    retailerAccount.retailers.forEach((retailer: Retailer) => {
      brandToRetailer[String(retailer.brand_id)] = String(retailer.id)
      retailerToBrand[String(retailer.id)] = String(retailer.brand_id)
    })
    return { brandToRetailer, retailerToBrand }
  }, [retailerAccount.retailers])

  return (
    <Toolbar disableGutters>
      <Accordion
        expanded={!isMobile ? true : isMobileOpen}
        square
        disableGutters
        sx={{
          'width': '100%',
          'border': 'none',
          'boxShadow': 'none',
          'background': 'none',
          '& .MuiAccordionSummary-content': {
            justifyContent: 'center',
          },
          '& .MuiAccordionDetails-root': {
            padding: 0,
          },
        }}
      >
        <AccordionSummary
          sx={{
            display: { xs: 'block', sm: 'none' },
          }}
        >
          <Button
            variant="text"
            sx={{ color: 'text.primary' }}
            startIcon={<FilterList fontSize="medium" />}
            onClick={() => setIsMobileOpen(!isMobileOpen)}
          >
            {I18n.t(`filters.${isMobileOpen ? 'hide' : 'show'}`)}{' '}
            {I18n.t('filters.filters')}
          </Button>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={3} alignContent="center">
            {!hideFilters.includes('brand') && (
              <Grid item sm={2} xs={12}>
                <RetailerAccountBrandAutocomplete
                  brandFilter={brandFilter}
                  value={
                    filterInfo.filter.brands
                      ? booleanFilter(
                          filterInfo.filter.brands.map(
                            (x) => idMap.retailerToBrand[x],
                          ),
                        )
                      : []
                  }
                  onChange={(e) =>
                    onChange(
                      'brands',
                      e.map((x) => String(idMap.brandToRetailer[x.id])),
                    )
                  }
                  slotProps={{
                    textField: {
                      label:
                        brandFilter === 'industry'
                          ? I18n.t('filters.selectIndustry')
                          : I18n.t('filters.selectBrands'),

                      sx: {
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                      },
                    },
                  }}
                  showAll
                />
              </Grid>
            )}
            {!hideFilters.includes('filterByChannel') && (
              <Grid item sm={2} xs={12}>
                <SearchableFilter
                  multiple
                  options={filterByChannel}
                  value={filterInfo.filter.filterByChannel}
                  label={I18n.t('filters.channel')}
                  onChange={(e) => onChange('filterByChannel', e)}
                  showAll
                />
              </Grid>
            )}
            {!hideFilters.includes('show') && (
              <Grid item sm={2} xs={12}>
                <SearchableFilter
                  multiple
                  options={showItems}
                  value={filterInfo.filter.showValue}
                  label={I18n.t('filters.show')}
                  onChange={(e) => onChange('showValue', e)}
                  showAll
                />
              </Grid>
            )}
            {!hideFilters.includes('tags') && (
              <Grid item sm={2} xs={12}>
                <SearchableFilter
                  options={tagsItems}
                  value={filterInfo.filter.tagsValues}
                  label={I18n.t('filters.tags')}
                  multiple
                  onChange={(e) => onChange('tagsValues', e)}
                  showAll
                />
              </Grid>
            )}
            {!hideFilters.includes('clear') && (
              <Grid
                item
                sm={2}
                xs={12}
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <div>
                  <Button
                    variant="text"
                    onClick={() => {
                      onClear()
                      // HACK: usePreviouslySelected[Brands/Retailers] causes a clearing issue so we need to manually clear the brands filter
                      onChange('brands', [])
                    }}
                  >
                    <Typography>{I18n.t('filters.clear')}</Typography>
                  </Button>
                </div>
              </Grid>
            )}
            {!hideFilters.includes('search') && (
              <Grid item sm={2} xs={12}>
                <Search onChange={onChangeSearchValue} />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Toolbar>
  )
}

export default ContentFilters
